import { Map as iMap, OrderedMap as iOrderedMap, List as iList, fromJS } from 'immutable';
import * as constants from './temp-connections-constants';

const createEmptyConnection = () => {
  return iMap({
    recordValid: false,
    companyName: '',
    email: '',
    firstName: '',
    lastName: '',
  });
};
const initialState = iMap({
  allRecordsValid: false,
  persistedFromServer: false,
  processed: false,
  emailValidLength: true,
  connections: iOrderedMap({
    [new Date() + Math.random()]: createEmptyConnection() }),
});

const validEmail = (email) => {
  const filter = /^\s*[\w\-\+_]+(\.[\w\-\+_]+)*\@[\w\-\+_]+\.[\w\-\+_]+(\.[\w\-\+_]+)*\s*$/;
  return String(email).search(filter) !== -1;
};

const checkIfValidRecord = (state, mapKey, valueKey, value) => {
  if (state.getIn(['connections', mapKey, 'email']) &&
    validEmail(state.getIn(['connections', mapKey, 'email'])) ||
    value && valueKey === 'email' &&
    validEmail(value) ||
    valueKey !== 'email' &&
    state.getIn(['connections', mapKey, 'email'])) {
    return true;
  }
  return false;
};

const checkIfAllRecordsValid = (state) => {
  return state.get('connections').every(record => record.get('recordValid'));
};

const changeValue = (state, payload) => {
  const { mapKey, valueKey, value } = payload;
  let focusValue;
  const recordValid = checkIfValidRecord(state, mapKey, valueKey, value);
  if (valueKey === 'companyName') focusValue = 'hasFocusedCompanyName';
  if (valueKey === 'email') focusValue = 'hasFocusedEmail';
  let newState = state
    .setIn(['connections', mapKey, valueKey], value)
    .setIn(['connections', mapKey, 'recordValid'], recordValid);
  if (focusValue) newState = newState.setIn(['connections', mapKey, focusValue], true);
  return newState.set('allRecordsValid', checkIfAllRecordsValid(newState));
};

const resetConnectionsToDefaults = () => {
  return initialState
    .set('isUsingInputs', false)
    .set('allInvitationsProcessed', false)
    .set('emailValue', '')
    .set('allRecordsValid', false);
};

const isEmptyConnection = (connection) => {
  return !(Boolean(connection.get('companyName'))
   || Boolean(connection.get('email'))
   || Boolean(connection.get('firstName'))
   || Boolean(connection.get('lastName')));
};

const setRowToSave = (state, row, index) => {
  let newState;
  if (state.get('connections').size === 1 &&
    state.get('connections').every((connection) => {
      return isEmptyConnection(connection);
    })
  ) {
    newState = state.set('connections', iOrderedMap({ [new Date() + Math.random() + index]: row }));
  } else {
    newState = state.setIn(['connections', (new Date() + Math.random() + index)], row);
  }
  return newState;
};


const convertFromCSV = (state, csv) => {
  const oldState = iMap(state);
  let newState;
  newState = state.set('previousState', oldState);
  const rows = iList(csv.split(/\r\n|\n|\r/));
  const headers = ['companyName', 'email', 'firstName', 'lastName'];
  let invalidRecords = iList();
  rows.forEach((row, rowIndex) => {
    const content = row.split(',');
    let rowToSave = iMap();
    let invalidRecord = iList();
    let errors = iMap();
    let allBlank = true;
    headers.forEach((item, columnIndex) => {
      const value = content[columnIndex] ? content[columnIndex].replace(/["]/g, '').trim() : '';
      if (value) allBlank = false;
      if (columnIndex === 1 && !validEmail(value)) {
        invalidRecord = rowToSave.size ? rowToSave.toList().push(value) : invalidRecord.push(value);
        errors = errors.set('line', rowIndex + 1).set('invalidEmail', true);
      } else if (invalidRecord.size) {
        invalidRecord = invalidRecord.push(value);
      } else {
        rowToSave = rowToSave.set(headers[columnIndex], value);
      }
    });
    if (!allBlank) {
      if (errors.size) invalidRecord = invalidRecord.push(errors);
      if (invalidRecord.size) {
        invalidRecords = invalidRecords.push(invalidRecord);
      } else {
        rowToSave = rowToSave.set('recordValid', true);
        newState = setRowToSave(newState, rowToSave, rowIndex);
      }
    }
  });
  if (invalidRecords.size) newState = newState.set('invalidRecords', invalidRecords);
  return newState.set('processed', true).set('allRecordsValid', checkIfAllRecordsValid(newState));
};

const checkIfAllProcessed = (state, invitations, allProccessed) => {
  // !! for type casting to a Bool
  const returnState = state.set('sendingEmails', false);
  if (allProccessed) return returnState.set('allInvitationsProcessed', true).set('processing', false);
  return returnState.set('allInvitationsProcessed', false).set('processing', false);
};

const setReceivedConnections = (state, payload) => {
  let newState = state.set('connections', iOrderedMap());

  const connectionInfo = iMap({
    recordValid: false,
  });

  let allInvitationsProcessed = true;

  if (!payload.invitations || !Array.isArray(payload.invitations)) {
    newState = newState.setIn(['connections', (new Date() + Math.random())], connectionInfo)
      .set('allRecordsValid', false)
      .set('allInvitationsProcessed', false);
  } else {
    payload.invitations.forEach((invitation, index) => {
      if (allInvitationsProcessed) {
        allInvitationsProcessed = (invitation && !!invitation.status && invitation.status.newState === 'processed');
      }
      newState = newState.setIn(['connections', (new Date() + Math.random() + index)], iMap({ // probably overkill on randomization but just in case;
        companyName: invitation.companyName,
        email: invitation.email,
        firstName: invitation.firstName,
        lastName: invitation.lastName,
        recordValid: Boolean(invitation.companyName) && Boolean(invitation.email),
      }));
    });
    newState = newState.set('processed', true)
    .set('allInvitationsProcessed', allInvitationsProcessed)
    .set('persistedFromServer', true)
    .set('allRecordsValid', checkIfAllRecordsValid(newState));
  }
  return newState;
};

export function tempConnections(state = initialState, action) {
  switch (action.type) {
    case 'CANCEL_CSV_HEADER_SELECTION':
      return resetConnectionsToDefaults();
    case 'CHANGE_EMAIL_VALUE':
      return state.set('emailValue', action.payload).set('emailValidLength', action.payload.length <= 500);
    case 'CHANGE_NEW_CONNECTION_VALUE':
      return changeValue(state, action.payload);
    case 'SEND_TEMP_CONNECTIONS':
      return checkIfAllProcessed(state, action.payload.invitations, action.payload.allProcessed);
    case 'IS_PERSISTED_FROM_SERVER':
      return state.set('persistedFromServer', true);
    case 'CONVERT_FROM_CSV':
      return convertFromCSV(state, action.payload);
    case 'DELETED_TEMP_CONNECTIONS':
      return state.set('persistedFromServer', false);
    case 'ACCEPTED_CSV_SELECTION':
      return state.delete('previousState').delete('invalidRecords');
    case 'CANCELED_CSV_SELECTION':
      return state.get('previousState').delete('previousState');
    case 'SEND_TEMP_CONNECTIONS_STARTING':
      return state.set('processing', true);
    case 'SEND_EMAIL_TO_TEMP_CONNECTIONS_STARTING':
      return state.set('sendingEmails', true);
    case 'RECEIVE_ADD_CONNECTIONS':
      return setReceivedConnections(state, action.payload);
    case 'REMOVE_ADD_INVITATION':
      const newState = state.deleteIn(['connections', action.payload]);
      return newState.set('allRecordsValid', checkIfAllRecordsValid(newState));
    case 'NEW_ADD_CONNECTIONS':
      return state.setIn(['connections', (new Date() + Math.random())], createEmptyConnection()).set('allRecordsValid', false).set('processed', false);
    case 'SEND_EMAIL_TO_TEMP_CONNECTIONS':
      return resetConnectionsToDefaults().set('report', action.payload.report).set('emailStatuses', iMap(action.payload.statuses)).set('sendingEmails', false);
    case constants.UPDATE_TEMP_CONN_PROP:
      return state.merge(fromJS(action.payload));
    case 'CLEAR_STORES':
      return initialState;
    default:
      return state;
  }
}



// WEBPACK FOOTER //
// ./src/app/stores/temp-connections/temp-connections-reducer.js